import { type FC, useEffect } from 'react';
import styles from '../styles/personal-information-entry.module.scss';
import type { PersonalInfoConfig, PersonalInfo } from '../models/repair-bookings';
import PageTitle from '../components/page-title';
import FormField from '../components/form-field';
import AttentionNotice from '../components/attention-notice';
import PageActions from '../components/page-actions';
import usePersonalInfo from '../hooks/use-personal-info';

const PersonalInformationEntry: FC<{
    info: PersonalInfo;
    personalInfoConfig: PersonalInfoConfig;
    handleChange: <K extends keyof PersonalInfo>(field: K, value: PersonalInfo[K]) => void;
    onComplete: () => void;
    onCancel: () => void;
}> = ({ info, personalInfoConfig, handleChange, onComplete, onCancel }) => {
    const { filled, errorMessage, defaultLineStatus, selectedLineStatus } = usePersonalInfo(
        info,
        personalInfoConfig
    );

    useEffect(() => {
        if (defaultLineStatus && info.line_status !== selectedLineStatus) {
            handleChange('line_status', selectedLineStatus);
        }
    }, [defaultLineStatus, personalInfoConfig, handleChange, info.line_status, selectedLineStatus]);

    const goNext = () => {
        if (errorMessage) {
            alert(errorMessage);
        } else {
            onComplete();
        }
    };

    return (
        <div className={styles.personalInformation}>
            <PageTitle title="お客さまの情報を入力してください" />
            <div className={styles.personalInfoForm}>
                <div className={styles.formRow}>
                    <FormField
                        inputType="text"
                        label="姓"
                        placeholder="例）英雄"
                        required={true}
                        value={info.last_name}
                        onChange={(value) => handleChange('last_name', value)}
                    />
                    <FormField
                        inputType="text"
                        label="名"
                        placeholder="例）三太郎"
                        value={info.first_name}
                        onChange={(value) => handleChange('first_name', value)}
                    />
                </div>
                <div className={styles.formRow}>
                    <FormField
                        inputType="text"
                        label="姓（カナ）"
                        constraintText="全角カタカナのみ"
                        placeholder="例）エイユウ"
                        required={true}
                        value={info.last_name_pronunciation}
                        onChange={(value) => handleChange('last_name_pronunciation', value)}
                    />
                    <FormField
                        inputType="text"
                        label="名（カナ）"
                        constraintText="全角カタカナのみ"
                        placeholder="例）サンタロウ"
                        value={info.first_name_pronunciation}
                        onChange={(value) => handleChange('first_name_pronunciation', value)}
                    />
                </div>
                {personalInfoConfig.granularity === 'extended' && (
                    <FormField
                        inputType="text"
                        label="生年月日"
                        placeholder="例）1971/07/19 昭和46/7/19（満XX歳）"
                        required={true}
                        value={info.birthday ?? ''}
                        onChange={(value) => handleChange('birthday', value)}
                    />
                )}
                <FormField
                    inputType="tel"
                    label="携帯電話番号"
                    constraintText="ハイフン含め半角のみ"
                    helperText={
                        personalInfoConfig.granularity === 'standard'
                            ? '※ au/UQ mobileをご利用中の場合、ご契約の携帯番号を記入ください'
                            : undefined
                    }
                    placeholder="例）000-0000-0000"
                    required={true}
                    value={info.phone_number}
                    onChange={(value) => handleChange('phone_number', value)}
                />
                {personalInfoConfig.granularity === 'extended' && (
                    <>
                        <FormField
                            inputType="select"
                            name="line_status"
                            label="au/UQ回線状況"
                            selectTitle="選択してください"
                            options={personalInfoConfig.lineStatuses.map((status) => ({
                                value: status.key,
                                label: status.name,
                            }))}
                            required={true}
                            value={selectedLineStatus}
                            onChange={(value) => handleChange('line_status', value)}
                        />
                        <FormField
                            inputType="text"
                            label="加入者コード"
                            required={true}
                            value={info.subscriber_code ?? ''}
                            onChange={(value) => handleChange('subscriber_code', value)}
                        />
                    </>
                )}
                <FormField
                    inputType="tel"
                    label="連絡先電話番号"
                    constraintText="ハイフン含め半角のみ"
                    placeholder="例）000-0000-0000"
                    value={info.alt_phone_number}
                    onChange={(value) => handleChange('alt_phone_number', value)}
                />
                <FormField
                    inputType="email"
                    label="電子メール"
                    placeholder="例）santaro@au.com"
                    required={personalInfoConfig.granularity === 'standard'}
                    value={info.email}
                    onChange={(value) => handleChange('email', value)}
                />
                <FormField
                    inputType="textarea"
                    label="店舗へのご連絡事項"
                    placeholder="店舗へのご要望など入力します（300文字まで）"
                    value={info.notes}
                    onChange={(value) => handleChange('notes', value)}
                />
                {personalInfoConfig.granularity === 'extended' && (
                    <label className={styles.warrantyMemberField}>
                        <input
                            type="checkbox"
                            className={styles.warrantyMemberCheckbox}
                            checked={!!info.is_warranty_member}
                            onChange={(e) => handleChange('is_warranty_member', e.target.checked)}
                        />
                        <span>故障紛失サポート会員の場合はチェック</span>
                    </label>
                )}
                <AttentionNotice note="※ ご登録いただいた電話番号、もしくはメールアドレスにご連絡を行うことがございますのでご了承ください。" />
            </div>

            <PageActions
                actionTitle="入力内容を確認する"
                actionButtonDisabled={!filled}
                onOk={goNext}
                onCancel={onCancel}
            />
        </div>
    );
};

export default PersonalInformationEntry;
